import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setToggleAuthModal } from "../../Redux/authReducer";
import {
  useResetPasswordMutation,
  useLazyValidateResetTokenQuery,
} from "../../Services/api.auth.slice";
import toast from "react-hot-toast";
import LoaderOverlay from "../../Components/Loader/Loader";

// Mirrors backend/src/validations/CustomValidation.js (PASSWORD_MIN/MAX +
// isPasswordPolicyValid). Can reject, never accept — the server decides.
const PASSWORD_MIN = 8;
const PASSWORD_MAX = 128;
const PASSWORD_HINT = "At least 8 characters, with an uppercase letter, a lowercase letter and a number";
const passwordProblem = (pw: string): string | null => {
  if (pw.length < PASSWORD_MIN) return `Password must be at least ${PASSWORD_MIN} characters long`;
  if (pw.length > PASSWORD_MAX) return `Password must be at most ${PASSWORD_MAX} characters long`;
  if (!/[a-z]/.test(pw) || !/[A-Z]/.test(pw) || !/\d/.test(pw)) {
    return "Password needs an uppercase letter, a lowercase letter, and a number";
  }
  return null;
};

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  // An account an admin created arrives here from its welcome email (Oct 20
  // call): same link, but they're choosing a password, not resetting one.
  const isWelcome = searchParams.get("welcome") === "1";
  const dispatch = useDispatch();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(true);

  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [validateToken] = useLazyValidateResetTokenQuery();

  useEffect(() => {
    const checkToken = async () => {
      if (!token || !email) {
        setTokenError(
          isWelcome
            ? "This link is missing information. Use the link from your welcome email exactly as sent."
            : "Invalid password reset link. Please request a new one."
        );
        setIsValidating(false);
        return;
      }

      try {
        const result = await validateToken({ token, email }).unwrap();
        if (result?.data?.valid) {
          setIsValidating(false);
        }
      } catch (error: any) {
        const errorMessage = isWelcome
          ? "This welcome link has expired or was already used."
          : error?.data?.errors?.message || "Invalid or expired reset link";
        setTokenError(errorMessage);
        setIsValidating(false);
      }
    };

    checkToken();
  }, [token, email, isWelcome, validateToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Full policy, not just length. The AUTHORITY is
    // backend/src/validations/CustomValidation.js — this checked length alone
    // and let "12345678" reach the server, which then rejected it for a rule
    // the form never mentioned.
    const problem = passwordProblem(password);
    if (problem) {
      toast.error(problem);
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!token || !email) {
      toast.error("Invalid reset link");
      return;
    }

    try {
      const response = await resetPassword({
        token,
        email,
        password,
        confirmPassword,
      }).unwrap();

      if (response?.data?.message) {
        if (isWelcome) {
          // Straight to sign-in: choosing a password was the last step.
          toast.success("Your password is set. Sign in to get started.");
          navigate("/");
          dispatch(setToggleAuthModal(true));
          return;
        }
        toast.success(response.data.message);
        navigate("/");
      }
    } catch (error: any) {
      const errorMessage =
        error?.data?.errors?.message || "Failed to reset password";
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return <LoaderOverlay />;
  }

  // Show loading while validating token
  if (isValidating) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "80vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: "100%", textAlign: "center" }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Checking your link...</Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Show error if token is invalid or expired
  if (tokenError) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "80vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {tokenError}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {isWelcome
                ? "Use “Forgot password” on the sign-in screen to get a new link."
                : "Please request a new password reset link."}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                navigate("/");
                if (isWelcome) dispatch(setToggleAuthModal(true));
              }}
            >
              {isWelcome ? "Go to sign in" : "Go to Home"}
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Typography variant="h5" component="h1" gutterBottom>
            {isWelcome ? "Welcome to SimplerHome" : "Reset Your Password"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} data-testid="reset-subtitle">
            {isWelcome
              ? `Your account for ${email} is ready. Choose a password to finish setting it up.`
              : "Enter your new password below."}
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={isWelcome ? "Password" : "New Password"}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              helperText={PASSWORD_HINT}
            />
            <TextField
              fullWidth
              label={isWelcome ? "Confirm password" : "Confirm New Password"}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              error={confirmPassword !== "" && password !== confirmPassword}
              helperText={
                confirmPassword !== "" && password !== confirmPassword
                  ? "Passwords do not match"
                  : ""
              }
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              sx={{ mt: 3 }}
              disabled={
                !password || !confirmPassword || password !== confirmPassword
              }
            >
              {isWelcome ? "Set password" : "Reset Password"}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
